import flatten from 'flat';
import config from '../config';
import {
  getTime,
  addMinutes,
  isDate,
  differenceInMilliseconds,
  endOfDay,
  startOfDay,
  format
} from 'date-fns';

export const isMS = value => {
  return value === parseInt(value, 10);
};

export const isNotMS = value => {
  return value !== parseInt(value, 10);
};

export const toUTC = value => {
  let date = new Date(value);
  return getTime(addMinutes(date, -date.getTimezoneOffset()));
};

export const fromUTC = value => {
  let date = new Date(value);
  return getTime(addMinutes(date, date.getTimezoneOffset()));
};

export const parseDateToUTC = (value, time = 'startOfDay') => {
  if (time !== 'startOfDay' && time !== 'endOfDay' && time !== 'current') {
    throw new Error('time argument must be one of: startOfDay, endOfDay, current');
  }
  if (value === undefined || value === null) return undefined;
  if (isNaN(value)) return value; // Сообщение об ошибке
  if (!isDate(new Date(value))) return value;
  let newValue = undefined;
  if (time === 'startOfDay') {
    newValue = toUTC(startOfDay(value));
  } else if (time === 'current') {
    const now = Date.now();
    const nowInUTC = toUTC(now);
    const startOfDayInUTC = toUTC(startOfDay(now));
    const offsetMS = differenceInMilliseconds(new Date(nowInUTC), new Date(startOfDayInUTC));
    newValue = toUTC(new Date(value + offsetMS));
  } else if (time === 'endOfDay') {
    newValue = toUTC(endOfDay(new Date(value)));
  }
  return newValue;
};

export const formatDateFromUTC = value => {
  if (!isMS(value)) return value;
  if (!isDate(new Date(value))) return value;
  return startOfDay(fromUTC(value));
};

export const getDate = ms => {
  if (!isMS(ms)) return null;
  return format(startOfDay(fromUTC(ms)), 'dd.MM.yyyy');
};

export const dateFieldRest = {
  parse: incomeValue => parseDateToUTC(incomeValue),
  format: outcomeValue => formatDateFromUTC(outcomeValue)
};

export function createBreakpoints() {
  const keys = ['xs', 'sm', 'md', 'lg'];

  const values = {
    xs: 0,
    sm: 768,
    md: 992,
    lg: 1200
  };

  const unit = 'px';

  const step = 5;

  function up(key) {
    const value = typeof values[key] === 'number' ? values[key] : key;
    return `@media (min-width:${value}${unit})`;
  }

  function down(key) {
    const endIndex = keys.indexOf(key) + 1;
    const upperbound = values[keys[endIndex]];

    if (endIndex === keys.length) {
      // xl down applies to all sizes
      return up('xs');
    }

    const value = typeof upperbound === 'number' && endIndex > 0 ? upperbound : key;
    return `@media (max-width:${value - step / 100}${unit})`;
  }

  function between(start, end) {
    const endIndex = keys.indexOf(end) + 1;

    if (endIndex === keys.length) {
      return up(start);
    }

    return (
      `@media (min-width:${values[start]}${unit}) and ` +
      `(max-width:${values[keys[endIndex]] - step / 100}${unit})`
    );
  }

  function only(key) {
    return between(key, key);
  }

  function width(key) {
    return values[key];
  }

  return {
    keys,
    values,
    up,
    down,
    between,
    only,
    width
  };
}

export function integer(value) {
  // eslint-disable-line
  if (value === '') return null;
  return Number(value.replace(/[^0-9]/g, ''));
}

export function digits(value) {
  // eslint-disable-line
  if (value === '') return null;
  return value.replace(/[^0-9]/g, '');
}

export function formatPhone(phone) {
  if (!phone) return '';
  phone = Number(phone.replace(/\D/g, ''));
  return '+' + phone.toString().replace(/(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/, '$1($2)$3-$4-$5');
}

export function formatSecretPhone(phone) {
  if (!phone) return '';
  phone = Number(phone.replace(/\D/g, ''));
  return '+' + phone.toString().replace(/(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/, '$1(***)***-***-$5');
}

export const getInputErrorByMeta = meta => {
  return ((meta.touched || meta.dirty) && meta.error) || (meta.submitFailed && meta.error);
};

export const langs = {
  ru: {
    regex: /^[а-яА-ЯёЁ-\s]+$/,
    error: 'Разрешены только русские буквы'
  },
  'not-en': {
    regex: /^[^a-zA-Z<>]+$/,
    error: 'Разрешены только русские буквы'
  },
  'not-ru': {
    regex: /^[^а-яА-ЯёЁ<>]+$/,
    error: 'Разрешены только латинские буквы'
  },
  en: {
    regex: /^[a-zA-Z-\s]+$/,
    error: 'Разрешены только латинские буквы'
  },
  'ru-en': {
    regex: /^[a-zA-Zа-яА-ЯёЁ-\s]+$/,
    error: 'Разрешены только русские и латинские буквы'
  },
  email: {
    regex: /^[a-zA-Z0-9._%+-@]+$/,
    error: 'Разрешены только латинские буквы'
  },
  digit: {
    regex: /^[0-9]+$/,
    error: 'Разрешены только цифры'
  }
};

export function focusOnFirstFieldWithError(errors) {
  const flatErrors = flatten(errors);
  const key = Object.keys(flatErrors).reduce((k, l) =>
    document.getElementById(`${k.replace(/\./g, '-')}-root`).getBoundingClientRect().top <
    document.getElementById(`${l.replace(/\./g, '-')}-root`).getBoundingClientRect().top
      ? k
      : l
  );

  const elem = document.getElementById(`${key}-root`);
  if (elem) {
    elem.scrollIntoView({ block: 'center', behaviour: 'smooth' });
  }
}

export function findDeltaWithinTwoArraysOfObjects(
  listOriginal,
  listNew,
  uniqueProp,
  returnProp = null
) {
  const delta = {
    added: [],
    removed: [],
    common: []
  };
  const _deltaMap = {};
  const _objHolder = {};
  listOriginal.forEach(function (item) {
    _objHolder[(uniqueProp && getItemValue(item, uniqueProp)) || item] = !returnProp
      ? item
      : item[returnProp] !== undefined
      ? item[returnProp]
      : item;
    _deltaMap[(uniqueProp && getItemValue(item, uniqueProp)) || item] = 'remove';
  });
  listNew.forEach(function (item) {
    _objHolder[(uniqueProp && getItemValue(item, uniqueProp)) || item] = !returnProp
      ? item
      : item[returnProp] !== undefined
      ? item[returnProp]
      : item;
    const d = _deltaMap[(uniqueProp && getItemValue(item, uniqueProp)) || item];
    if (Boolean(d) && d === 'remove') {
      _deltaMap[(uniqueProp && getItemValue(item, uniqueProp)) || item] = 'noChange';
    } else {
      _deltaMap[(uniqueProp && getItemValue(item, uniqueProp)) || item] = 'added';
    }
  });
  Object.keys(_deltaMap).forEach(function (id) {
    if (_deltaMap[id] === 'remove') delta.removed.push(_objHolder[id]);
    else if (_deltaMap[id] === 'added') delta.added.push(_objHolder[id]);
    else if (_deltaMap[id] === 'noChange') delta.common.push(_objHolder[id]);
  });
  return delta;
}

function getItemValue(item, uniqueProp) {
  if (typeof uniqueProp === 'string') {
    return item[uniqueProp];
  } else if (typeof uniqueProp === 'function') {
    return uniqueProp(item);
  }
}

export function makeCurrency(str, full = true) {
  const long = {
    usd: 'USD',
    rur: 'RUR',
    eur: 'EUR'
  };

  const short = {
    usd: '$',
    rur: '₽',
    eur: '€'
  };

  const source = full ? long : short;

  if (str.toUpperCase() === 'RUR') {
    return source.rur;
  } else if (str.toUpperCase() === 'RUB') {
    return source.rur;
  } else if (str.toLowerCase().indexOf('рубл') !== -1) {
    return source.rur;
  } else if (str.toUpperCase() === 'USD') {
    return source.usd;
  } else if (str.toLowerCase().indexOf('долл') !== -1) {
    return source.usd;
  } else if (str.toUpperCase() === 'EUR') {
    return source.eur;
  } else if (str.toLowerCase().indexOf('евро') !== -1) {
    return source.eur;
  }

  return str;
}

export const humanFileSize = (bytes, si) => {
  var thresh = si ? 1000 : 1024;
  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }
  var units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  var u = -1;
  do {
    bytes /= thresh;
    ++u;
  } while (Math.abs(bytes) >= thresh && u < units.length - 1);
  return bytes.toFixed(1) + ' ' + units[u];
};

export const downloadFile = params => {
  try {
    var downloadForm = document.createElement('form');
    downloadForm.setAttribute('id', 'lkkDownloadForm');
    downloadForm.setAttribute(
      'action',
      `${config.downloadOffset}assets/system/os/components/attachments/operations.php`
    );
    downloadForm.setAttribute('method', 'post');
    downloadForm.setAttribute('hidden', 'true');
    for (var key in params) {
      let kp = params[key];
      if (key === 'data') {
      } else {
        if (typeof kp === 'string') {
          kp = kp.replace(/"/g, "'");
        }
      }
      const input = document.createElement('input');
      input.setAttribute('type', 'text');
      input.setAttribute('name', key);
      input.setAttribute('value', kp);
      downloadForm.appendChild(input);
    }
    const operation = document.createElement('input');
    operation.setAttribute('type', 'text');
    operation.setAttribute('name', 'operation');
    operation.setAttribute('value', 'uploadFile');
    downloadForm.appendChild(operation);
    document.body.appendChild(downloadForm);
    downloadForm.submit();
    const elem = document.getElementById('lkkDownloadForm');
    elem.parentNode.removeChild(elem);
  } catch (error) {
    console.log(error.message);
  }
};

export const capitalize = (str, lower = false) =>
  (lower ? str.toLowerCase() : str).replace(/(?:^|\s|-|["'([{])+\S/g, match => match.toUpperCase());

export const collectPassport = passport => {
  const { seria, number, doi, issued, issuedCode } = passport;
  let collect = '';
  if (!!seria) collect += seria + ' ';
  if (!!number) collect += number;
  if (isMS(doi) || !!issued) {
    collect += ', выдан';
    if (issued) {
      collect += ' ' + issued;
      if (!!issuedCode) {
        collect += ' ' + issuedCode;
      }
      if (isMS(doi)) {
        collect += ',';
      }
    }
    if (isMS(doi)) {
      collect += ` ${format(new Date(doi), 'dd.MM.yyyy')}`;
    }
  }

  return collect.trim();
};

export const money = (
  sum = 0,
  currency = 'RUB',
  currencyDisplay = 'symbol',
  maximumFractionDigits = 0
) => {
  let cur = currency;
  if (cur === 'RUR') cur = 'RUB';
  const formatter = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: cur,
    currencyDisplay,
    maximumFractionDigits
  });

  return formatter.format(parseFloat(sum));
};

export const formatNumber = (sum = 0, minimumFractionDigits, maximumFractionDigits) => {
  const formatter = new Intl.NumberFormat('ru-RU', {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits
  });

  return formatter.format(parseFloat(sum));
};

export const getNoun = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};
